import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import ConfirmModal from './ConfirmModal';
import './DiagnosticPlots.css';

function DiagnosticPlots({ sessionId, onComplete, onBack }) {
  const [plotData, setPlotData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('confusion_matrix');
  const [datasetType, setDatasetType] = useState('test'); // 'test' (validation) or 'train'
  const [classType, setClassType] = useState('backsplash'); // 'backsplash' or 'infalling'
  const [threshold, setThreshold] = useState(0.5);
  const [currentThreshold, setCurrentThreshold] = useState(null); // Clicked threshold
  const [bestThreshold, setBestThreshold] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Define all diagnostic tabs
  const diagnosticTabs = [
    { key: 'confusion_matrix', title: 'Confusion Matrix', enabled: true },
    { key: 'accuracy', title: 'Accuracy', enabled: true },
    { key: 'feature_importance', title: 'Feature Importance', enabled: true },
    { key: 'auc', title: 'AUC', enabled: true },
    { key: 'backsplash_fraction', title: 'Backsplash Fraction', enabled: true }
  ];

  // Determine if current tab uses dataset toggle
  const usesDatasetToggle = (tabKey) => {
    return ['confusion_matrix', 'feature_importance', 'accuracy', 'auc'].includes(tabKey);
  };

  // Determine if current tab uses class type toggle
  const usesClassToggle = (tabKey) => {
    return ['accuracy', 'auc'].includes(tabKey);
  };

  // Initial load
  useEffect(() => {
    fetchPlots();
  }, [sessionId]);

  // When dataset changes: re-fetch plots for the new dataset
  useEffect(() => {
    if (sessionId) {
      fetchConfusionMatrix();
      fetchAccuracy();
      fetchAuc();
      
      // When switching to train for the first time, also pre-fetch the other class type
      if (datasetType === 'train') {
        const otherClassType = classType === 'backsplash' ? 'infalling' : 'backsplash';
        fetchAccuracyForParams('train', otherClassType);
        fetchAucForParams('train', otherClassType);
      }
    }
  }, [datasetType]);

  // When class type changes: re-fetch accuracy and AUC
  useEffect(() => {
    if (sessionId) {
      fetchAccuracy();
      fetchAuc();
    }
  }, [classType]);

  // Update accuracy plot threshold line when threshold changes (visual only, no re-fetch)
  useEffect(() => {
    const accuracyCacheKey = `accuracy_${datasetType}_${classType}`;
    if (plotData[accuracyCacheKey] && activeTab === 'accuracy') {
      updateAccuracyPlotThreshold(threshold);
    }
    // Also update AUC plot markers when threshold changes (from slider)
    const aucCacheKey = `auc_${datasetType}_${classType}`;
    if (plotData[aucCacheKey] && activeTab === 'auc') {
      updateAucPlotByThreshold(threshold, true);
    }
  }, [threshold]);

  const fetchPlots = async () => {
    try {
      setError(null);
      
      // Fetch best threshold first
      try {
        const thresholdResponse = await axios.get(`/api/best-threshold/${sessionId}`);
        if (bestThreshold === null && thresholdResponse.data.best_threshold) {
          const best = thresholdResponse.data.best_threshold;
          setBestThreshold(best);
          setThreshold(best);
        }
      } catch (err) {
        console.error('Error fetching best threshold:', err);
      }
      
      // Fetch confusion matrix first and immediately stop loading state
      setLoading(true);
      await fetchConfusionMatrix(); // test confusion matrix
      setLoading(false); // Allow UI to display confusion matrix immediately
      
      // Continue fetching remaining plots in background (non-blocking)
      // Only fetch test/validation plots initially
      fetchAccuracy(); // test backsplash accuracy
      fetchFeatureImportance(); // feature importance
      fetchAuc(); // test backsplash AUC
      fetchBacksplashFraction(); // backsplash fraction plot
      
      // Pre-load infalling class for test set only
      fetchAccuracyForParams('test', 'infalling');
      fetchAucForParams('test', 'infalling');
      
      // Train set plots will be loaded lazily when train toggle is first activated
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load diagnostic plots');
      setLoading(false);
    }
  };

  const fetchConfusionMatrix = async () => {
    try {
      const response = await axios.get(`/api/plots/${sessionId}/confusion_matrix`, {
        params: { dataset_type: datasetType }
      });
      setPlotData(prev => ({ ...prev, 'confusion_matrix': response.data }));
    } catch (err) {
      console.error('Error fetching confusion matrix:', err);
    }
  };

  const fetchConfusionMatrixForParams = async (dataset) => {
    try {
      const response = await axios.get(`/api/plots/${sessionId}/confusion_matrix`, {
        params: { dataset_type: dataset }
      });
      setPlotData(prev => ({ ...prev, 'confusion_matrix': response.data }));
    } catch (err) {
      console.error(`Error fetching confusion matrix for ${dataset}:`, err);
    }
  };

  const fetchAccuracy = async () => {
    try {
      const response = await axios.get(`/api/plots/${sessionId}/accuracy`, {
        params: { dataset_type: datasetType, class_type: classType }
      });
      const cacheKey = `accuracy_${datasetType}_${classType}`;
      setPlotData(prev => ({ ...prev, [cacheKey]: response.data }));
      
      // Add threshold lines after it loads
      if (threshold !== null) {
        setTimeout(() => updateAccuracyPlotThreshold(threshold), 0);
      }
    } catch (err) {
      console.error('Error fetching accuracy:', err);
    }
  };

  const fetchAccuracyForParams = async (dataset, classTypeParam) => {
    try {
      const response = await axios.get(`/api/plots/${sessionId}/accuracy`, {
        params: { dataset_type: dataset, class_type: classTypeParam }
      });
      const cacheKey = `accuracy_${dataset}_${classTypeParam}`;
      setPlotData(prev => ({ ...prev, [cacheKey]: response.data }));
    } catch (err) {
      console.error(`Error fetching accuracy for ${dataset}/${classTypeParam}:`, err);
    }
  };

  const fetchFeatureImportance = async () => {
    try {
      const response = await axios.get(`/api/plots/${sessionId}/feature_importance`);
      setPlotData(prev => ({ ...prev, 'feature_importance': response.data }));
    } catch (err) {
      console.error('Error fetching feature importance:', err);
    }
  };

  const fetchAuc = async () => {
    try {
      const response = await axios.get(`/api/plots/${sessionId}/auc`, {
        params: { dataset_type: datasetType, class_type: classType }
      });
      const cacheKey = `auc_${datasetType}_${classType}`;
      setPlotData(prev => ({ ...prev, [cacheKey]: response.data }));
    } catch (err) {
      console.error('Error fetching AUC:', err);
    }
  };

  const fetchAucForParams = async (dataset, classTypeParam) => {
    try {
      const response = await axios.get(`/api/plots/${sessionId}/auc`, {
        params: { dataset_type: dataset, class_type: classTypeParam }
      });
      const cacheKey = `auc_${dataset}_${classTypeParam}`;
      setPlotData(prev => ({ ...prev, [cacheKey]: response.data }));
    } catch (err) {
      console.error(`Error fetching AUC for ${dataset}/${classTypeParam}:`, err);
    }
  };

  const fetchBacksplashFraction = async (thresholdParam = null) => {
    try {
      const params = {};
      if (thresholdParam !== null) {
        params.threshold = thresholdParam;
      }
      const response = await axios.get(`/api/plots/${sessionId}/backsplash_fraction`, { params });
      setPlotData(prev => ({ ...prev, 'backsplash_fraction': response.data }));
    } catch (err) {
      console.error('Error fetching backsplash fraction:', err);
    }
  };

  const handleSetBestThreshold = () => {
    if (bestThreshold !== null) {
      setThreshold(bestThreshold);
    }
  };

  const handleThresholdChange = async (newThreshold) => {
    setThreshold(newThreshold);
    
    // Fetch confusion matrix with new threshold in background
    try {
      const confusionResponse = await axios.get(`/api/plots/${sessionId}/confusion_matrix`, {
        params: {
          dataset_type: datasetType,
          threshold: newThreshold
        }
      });
      
      setPlotData(prev => ({
        ...prev,
        'confusion_matrix': confusionResponse.data
      }));
    } catch (err) {
      console.error('Failed to fetch confusion matrix:', err);
    }
    
    // Fetch backsplash fraction plot with new threshold
    fetchBacksplashFraction(newThreshold);
  };

  const updateAccuracyPlotThreshold = (hoverThreshold) => {
    const accuracyCacheKey = `accuracy_${datasetType}_${classType}`;
    const accuracyPlotData = plotData[accuracyCacheKey];
    if (!accuracyPlotData) return;
    
    // Clone the plot data
    const updatedPlot = JSON.parse(JSON.stringify(accuracyPlotData));
    
    // Ensure shapes and annotations exist
    if (!updatedPlot.layout.shapes) {
      updatedPlot.layout.shapes = [];
    }
    if (!updatedPlot.layout.annotations) {
      updatedPlot.layout.annotations = [];
    }
    
    // 1. Update "New threshold" line (follows hover) - thin grey line
    let newThresholdShapeIndex = updatedPlot.layout.shapes.findIndex(
      shape => shape.line?.color && shape.line.color.includes('156, 163, 175') // Grey
    );
    
    if (newThresholdShapeIndex >= 0) {
      updatedPlot.layout.shapes[newThresholdShapeIndex].x0 = hoverThreshold;
      updatedPlot.layout.shapes[newThresholdShapeIndex].x1 = hoverThreshold;
    } else {
      updatedPlot.layout.shapes.push({
        type: 'line',
        x0: hoverThreshold,
        x1: hoverThreshold,
        y0: 0,
        y1: 1,
        yref: 'paper',
        line: {
          color: 'rgba(156, 163, 175, 0.5)', // Grey
          width: 1,
          dash: 'solid'
        }
      });
    }
    
    let newThresholdAnnIndex = updatedPlot.layout.annotations.findIndex(
      ann => ann.text && ann.text.includes('New:')
    );
    
    if (newThresholdAnnIndex >= 0) {
      updatedPlot.layout.annotations[newThresholdAnnIndex].x = hoverThreshold;
      updatedPlot.layout.annotations[newThresholdAnnIndex].text = `New: ${hoverThreshold.toFixed(3)}`;
    } else {
      updatedPlot.layout.annotations.push({
        x: hoverThreshold,
        y: 1,
        yref: 'paper',
        text: `New: ${hoverThreshold.toFixed(3)}`,
        showarrow: false,
        textangle: -90,
        xshift: -10,
        yshift: -10,
        font: {
          color: 'rgba(156, 163, 175, 0.8)',
          size: 11
        }
      });
    }
    
    // 2. Update or add "Current threshold" line (stays on click) - white solid line
    if (currentThreshold !== null) {
      let currentThresholdShapeIndex = updatedPlot.layout.shapes.findIndex(
        shape => shape.line?.color && shape.line.color.includes('255, 255, 255')
      );
      
      if (currentThresholdShapeIndex >= 0) {
        updatedPlot.layout.shapes[currentThresholdShapeIndex].x0 = currentThreshold;
        updatedPlot.layout.shapes[currentThresholdShapeIndex].x1 = currentThreshold;
      } else {
        updatedPlot.layout.shapes.push({
          type: 'line',
          x0: currentThreshold,
          x1: currentThreshold,
          y0: 0,
          y1: 1,
          yref: 'paper',
          line: {
            color: 'rgba(255, 255, 255, 0.6)',
            width: 2,
            dash: 'solid'
          }
        });
      }
      
      let currentThresholdAnnIndex = updatedPlot.layout.annotations.findIndex(
        ann => ann.text && ann.text.includes('Current:')
      );
      
      if (currentThresholdAnnIndex >= 0) {
        updatedPlot.layout.annotations[currentThresholdAnnIndex].x = currentThreshold;
        updatedPlot.layout.annotations[currentThresholdAnnIndex].text = `Current: ${currentThreshold.toFixed(3)}`;
      } else {
        updatedPlot.layout.annotations.push({
          x: currentThreshold,
          y: 1,
          yref: 'paper',
          text: `Current: ${currentThreshold.toFixed(3)}`,
          showarrow: false,
          textangle: -90,
          yshift: -10,
          xshift: -10,
          font: {
            color: 'rgba(255, 255, 255, 0.9)',
            size: 11
          }
        });
      }
    }
    
    setPlotData(prev => ({
      ...prev,
      [accuracyCacheKey]: updatedPlot
    }));
  };

  const updateAucPlotByFpr = (fpr, isCurrentThreshold = false) => {
    const aucCacheKey = `auc_${datasetType}_${classType}`;
    const aucPlotData = plotData[aucCacheKey];
    if (!aucPlotData || !aucPlotData.roc_data || !aucPlotData.thresholds) return;
    
    // Clone the plot data
    const updatedPlot = JSON.parse(JSON.stringify(aucPlotData));
    const rocData = updatedPlot.roc_data;
    const thresholds = updatedPlot.thresholds;
    
    // Find the index by FPR (use current class curve)
    const fprData = rocData[classType].fpr;
    let idx = 0;
    let minDiff = Math.abs(fprData[0] - fpr);
    for (let i = 1; i < fprData.length; i++) {
      const diff = Math.abs(fprData[i] - fpr);
      if (diff < minDiff) {
        minDiff = diff;
        idx = i;
      }
    }
    
    // Get the threshold at this index
    const thresholdAtFpr = thresholds[idx];
    
    // Find the trace indices for current and new markers
    // Traces are: [upper, lower, line, best, diagonal, current, new]
    const numTraces = updatedPlot.data.length;
    const newIdx = numTraces - 1;  // new marker
    const currentIdx = numTraces - 2;  // current marker
    
    // Find annotation indices: [Best, AUC, Current, New] - Current is index 2, New is index 3
    const annotations = updatedPlot.layout.annotations || [];
    const currentLabelIdx = annotations.findIndex(a => a.name === 'current_label');
    const newLabelIdx = annotations.findIndex(a => a.name === 'new_label');
    
    if (isCurrentThreshold) {
      // Update current threshold marker (white)
      updatedPlot.data[currentIdx].x = [rocData[classType].fpr[idx]];
      updatedPlot.data[currentIdx].y = [rocData[classType].tpr[idx]];
      
      // Update "Current" label position
      if (currentLabelIdx >= 0) {
        updatedPlot.layout.annotations[currentLabelIdx].x = rocData[classType].fpr[idx];
        updatedPlot.layout.annotations[currentLabelIdx].y = rocData[classType].tpr[idx] + 0.06;
      }
      
      // Update the threshold slider
      setThreshold(thresholdAtFpr);
      setCurrentThreshold(thresholdAtFpr);
    } else {
      // Update new threshold marker (gray)
      updatedPlot.data[newIdx].x = [rocData[classType].fpr[idx]];
      updatedPlot.data[newIdx].y = [rocData[classType].tpr[idx]];
      
      // Update "New" label position
      if (newLabelIdx >= 0) {
        updatedPlot.layout.annotations[newLabelIdx].x = rocData[classType].fpr[idx]-0.02;
        updatedPlot.layout.annotations[newLabelIdx].y = rocData[classType].tpr[idx];
      }
    }
    
    setPlotData(prev => ({
      ...prev,
      [aucCacheKey]: updatedPlot
    }));
    
    return thresholdAtFpr;
  };

  // Helper to update AUC plot by threshold (for slider changes)
  const updateAucPlotByThreshold = (newThreshold, isCurrentThreshold = false) => {
    const aucCacheKey = `auc_${datasetType}_${classType}`;
    const aucPlotData = plotData[aucCacheKey];
    if (!aucPlotData || !aucPlotData.roc_data || !aucPlotData.thresholds) return;
    
    // Find the index for this threshold
    const thresholds = aucPlotData.thresholds;
    let idx = 0;
    let minDiff = Math.abs(thresholds[0] - newThreshold);
    for (let i = 1; i < thresholds.length; i++) {
      const diff = Math.abs(thresholds[i] - newThreshold);
      if (diff < minDiff) {
        minDiff = diff;
        idx = i;
      }
    }
    
    // Clone and update directly (don't call updateAucPlotByFpr to avoid recursion with setThreshold)
    const updatedPlot = JSON.parse(JSON.stringify(aucPlotData));
    const rocData = updatedPlot.roc_data;
    const numTraces = updatedPlot.data.length;
    const newIdx = numTraces - 1;
    const currentIdx = numTraces - 2;
    
    // Find annotation indices
    const annotations = updatedPlot.layout.annotations || [];
    const currentLabelIdx = annotations.findIndex(a => a.name === 'current_label');
    const newLabelIdx = annotations.findIndex(a => a.name === 'new_label');
    
    if (isCurrentThreshold) {
      updatedPlot.data[currentIdx].x = [rocData[classType].fpr[idx]];
      updatedPlot.data[currentIdx].y = [rocData[classType].tpr[idx]];
      
      // Update "Current" label position
      if (currentLabelIdx >= 0) {
        updatedPlot.layout.annotations[currentLabelIdx].x = rocData[classType].fpr[idx];
        updatedPlot.layout.annotations[currentLabelIdx].y = rocData[classType].tpr[idx] - 0.02;
      }
    } else {
      updatedPlot.data[newIdx].x = [rocData[classType].fpr[idx]];
      updatedPlot.data[newIdx].y = [rocData[classType].tpr[idx]];
      
      // Update "New" label position
      if (newLabelIdx >= 0) {
        updatedPlot.layout.annotations[newLabelIdx].x = rocData[classType].fpr[idx];
        updatedPlot.layout.annotations[newLabelIdx].y = rocData[classType].tpr[idx] - 0.02;
      }
    }
    
    setPlotData(prev => ({
      ...prev,
      [aucCacheKey]: updatedPlot
    }));
  };

  const handlePlotClick = async (event) => {
    // Only handle clicks on the accuracy or auc tab
    if (activeTab !== 'accuracy' && activeTab !== 'auc') return;
    
    // With hovermode='x unified', clicks anywhere on the plot give us x coordinate
    if (!event?.points || event.points.length === 0) return;
    
    let clickedThreshold;
    
    if (activeTab === 'accuracy') {
      // For accuracy plot, x-axis is threshold
      clickedThreshold = event.points[0].x;
    } else if (activeTab === 'auc') {
      // For AUC plot, track by FPR (x-axis value) and convert to threshold
      const fpr = event.points[0].x;
      if (fpr === undefined || fpr === null) return;
      
      // Update the AUC plot current markers and get the threshold
      clickedThreshold = updateAucPlotByFpr(fpr, true);
      if (!clickedThreshold) return;
    }
    
    if (clickedThreshold === undefined || clickedThreshold === null) return;
    
    // Set current threshold (this will stay fixed until next click)
    setCurrentThreshold(clickedThreshold);
    
    // Update threshold slider
    setThreshold(clickedThreshold);
    
    // Pre-fetch confusion matrix in the background without showing loading state
    try {
      const confusionResponse = await axios.get(`/api/plots/${sessionId}/confusion_matrix`, {
        params: {
          dataset_type: datasetType,
          threshold: clickedThreshold
        }
      });
      
      setPlotData(prev => ({
        ...prev,
        'confusion_matrix': confusionResponse.data
      }));
    } catch (err) {
      console.error('Failed to pre-fetch confusion matrix:', err);
    }
    
    // Also fetch backsplash fraction plot with new threshold
    fetchBacksplashFraction(clickedThreshold);
  };

  const handlePlotHover = (event) => {
    // Only handle hover on the accuracy or auc tab
    if (activeTab !== 'accuracy' && activeTab !== 'auc') return;
    
    // Get the hovered coordinate
    if (event?.points && event.points.length > 0) {
      if (activeTab === 'accuracy') {
        const hoveredThreshold = event.points[0].x;
        updateAccuracyPlotThreshold(hoveredThreshold);
      } else if (activeTab === 'auc') {
        // For AUC plot, track by FPR (x-axis value)
        const point = event.points[0];
        const fpr = point.x;
        
        if (fpr !== undefined && fpr !== null) {
          updateAucPlotByFpr(fpr, false);
        }
      }
    }
  };

  const handleContinue = () => {
    setShowConfirmModal(true);
  };
  
  const handleConfirmContinue = () => {
    setShowConfirmModal(false);
    onComplete({ threshold });
  };
  
  const handleCancelContinue = () => {
    setShowConfirmModal(false);
  };

  // Highlight corresponding points in backsplash fraction plot (true, predicted, residual)
  const handleBacksplashFractionHover = (event) => {
    if (!event?.points || event.points.length === 0) return;
    
    const point = event.points[0];
    const pointIndex = point.pointIndex;
    const curveNumber = point.curveNumber;
    
    const bsFractionData = plotData['backsplash_fraction'];
    if (!bsFractionData) return;
    
    // Determine which curves correspond to this dataset
    // Traces order: 0=train_true, 1=train_pred, 2=test_true, 3=test_pred, 4=train_resid, 5=test_resid
    // Optional: 6=user_data (if present)
    // Highlight traces (always last 3): highlight_true, highlight_pred, highlight_resid
    let trueCurve, predCurve, residualCurve;
    
    if (curveNumber === 0 || curveNumber === 1 || curveNumber === 4) {
      // Training data
      trueCurve = 0;
      predCurve = 1;
      residualCurve = 4;
    } else if (curveNumber === 2 || curveNumber === 3 || curveNumber === 5) {
      // Test/validation data
      trueCurve = 2;
      predCurve = 3;
      residualCurve = 5;
    } else {
      // Skip user data or highlight markers themselves
      return;
    }
    
    // Get x value from the hovered point
    const xVal = point.x;
    const yVal = point.y;
    
    // Get corresponding values from customdata
    // customdata for train/test true contains: [clus_id, pred_fraction]
    // customdata for train/test pred contains: [clus_id, true_fraction]
    let yTrue, yPred, yResid;
    
    if (curveNumber === trueCurve) {
      // Hovering on true point - y is true, customdata[1] is pred
      yTrue = yVal;
      yPred = point.customdata ? point.customdata[1] : yVal;
    } else if (curveNumber === predCurve) {
      // Hovering on pred point - y is pred, customdata[1] is true
      yPred = yVal;
      yTrue = point.customdata ? point.customdata[1] : yVal;
    } else {
      // Hovering on residual point
      yResid = yVal;
      // Can't get true/pred from residual, use placeholder
      yTrue = 0;
      yPred = 0;
    }
    
    // Calculate residual
    yResid = yPred - yTrue;
    
    console.log('Hover - curveNumber:', curveNumber, 'pointIndex:', pointIndex);
    console.log('Values - xVal:', xVal, 'yTrue:', yTrue, 'yPred:', yPred, 'yResid:', yResid);
    
    // Clone the plot data
    const updatedPlot = JSON.parse(JSON.stringify(bsFractionData));
    
    // Find the highlight trace indices (last 3 traces)
    const numTraces = updatedPlot.data.length;
    const highlightTrueIdx = numTraces - 3;
    const highlightPredIdx = numTraces - 2;
    const highlightResidIdx = numTraces - 1;
    
    // Verify these are actually the highlight traces
    if (!updatedPlot.data[highlightTrueIdx] || 
        !updatedPlot.data[highlightPredIdx] || 
        !updatedPlot.data[highlightResidIdx]) {
      return;
    }
    
    // Update highlight marker positions and make them visible
    updatedPlot.data[highlightTrueIdx].x[0] = xVal;
    updatedPlot.data[highlightTrueIdx].y[0] = yTrue;
    updatedPlot.data[highlightTrueIdx].marker.opacity = 1;
    
    updatedPlot.data[highlightPredIdx].x[0] = xVal;
    updatedPlot.data[highlightPredIdx].y[0] = yPred;
    updatedPlot.data[highlightPredIdx].marker.opacity = 1;
    
    updatedPlot.data[highlightResidIdx].x[0] = xVal;
    updatedPlot.data[highlightResidIdx].y[0] = yResid;
    updatedPlot.data[highlightResidIdx].marker.opacity = 1;
    
    setPlotData(prev => ({
      ...prev,
      'backsplash_fraction': updatedPlot
    }));
  };

  const handleBacksplashFractionUnhover = () => {
    const bsFractionData = plotData['backsplash_fraction'];
    if (!bsFractionData) return;
    
    // Clone and hide the highlight markers
    const updatedPlot = JSON.parse(JSON.stringify(bsFractionData));
    
    const numTraces = updatedPlot.data.length;
    if (numTraces >= 3) {
      const highlightTrueIdx = numTraces - 3;
      const highlightPredIdx = numTraces - 2;
      const highlightResidIdx = numTraces - 1;
      
      // Hide markers by setting opacity to 0
      if (updatedPlot.data[highlightTrueIdx]) {
        updatedPlot.data[highlightTrueIdx].marker.opacity = 0;
      }
      if (updatedPlot.data[highlightPredIdx]) {
        updatedPlot.data[highlightPredIdx].marker.opacity = 0;
      }
      if (updatedPlot.data[highlightResidIdx]) {
        updatedPlot.data[highlightResidIdx].marker.opacity = 0;
      }
    }
    
    setPlotData(prev => ({
      ...prev,
      'backsplash_fraction': updatedPlot
    }));
  };

  return (
    <div className="diagnostic-plots">
      <div className="header-section">
        <div className="controls-description">
          <p className="controls-text">
            These plots show how well your model performs. Toggle between validation data to test on new galaxies 
            or training data to check for overfitting. View metrics for each galaxy type separately, since dataset 
            imbalance means completeness and purity differ between backsplash and infalling galaxies. Use this to 
            pick the best classification threshold for your research goals.
          </p>
        </div>
        
        <div className="controls-row">
          <div className="threshold-slider-section">
            <div className="threshold-slider-header">
              <label className="threshold-slider-label">Classification Threshold: {threshold.toFixed(4)}</label>
              <div className="threshold-buttons">
                <button 
                  className="best-threshold-btn"
                  onClick={handleSetBestThreshold}
                  disabled={bestThreshold === null}
                  title={bestThreshold ? `Set to best threshold: ${bestThreshold.toFixed(4)}` : 'Loading...'}
                >
                  Best ({bestThreshold ? bestThreshold.toFixed(4) : '...'})
                </button>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.001"
              value={threshold}
              onChange={(e) => handleThresholdChange(parseFloat(e.target.value))}
              className="threshold-slider"
            />
            <div className="threshold-slider-markers">
              <span>0.0</span>
              <span>0.25</span>
              <span>0.5</span>
              <span>0.75</span>
              <span>1.0</span>
            </div>
          </div>
          
          <div className="controls-section">
            <div className="toggle-group">
              <div className="toggle-buttons">
                <button 
                  className={`toggle-btn ${datasetType === 'test' ? 'active' : ''}`}
                  onClick={() => setDatasetType('test')}
                >
                  Validation
                </button>
                <button 
                  className={`toggle-btn ${datasetType === 'train' ? 'active' : ''}`}
                  onClick={() => setDatasetType('train')}
                >
                  Training
                </button>
              </div>
            </div>
            
            <div className="toggle-group">
              <div className="toggle-buttons">
                <button 
                  className={`toggle-btn ${classType === 'backsplash' ? 'active' : ''}`}
                  onClick={() => setClassType('backsplash')}
                >
                  Backsplash
                </button>
                <button 
                  className={`toggle-btn ${classType === 'infalling' ? 'active' : ''}`}
                  onClick={() => setClassType('infalling')}
                >
                  Infalling
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="diagnostic-tabs">
        <div className="tabs-header">
          {diagnosticTabs.map((tab) => (
            <button
              key={tab.key}
              className={`tab-button ${activeTab === tab.key ? 'active' : ''} ${!tab.enabled ? 'disabled' : ''}`}
              onClick={() => tab.enabled && setActiveTab(tab.key)}
              disabled={!tab.enabled}
            >
              {tab.title}
              {!tab.enabled && <span className="coming-soon-badge">Coming Soon</span>}
            </button>
          ))}
        </div>

        {(activeTab === 'accuracy' || activeTab === 'auc') && (
          <div style={{ 
            textAlign: 'center', 
            margin: '10px 0', 
            padding: '8px', 
            color: 'rgba(224, 224, 255, 0.85)',
            fontSize: '13px'
          }}>
            💡 Click anywhere on the plot to set that threshold for the confusion matrix
          </div>
        )}

        <div className="tab-content">
          {loading ? (
            <div className="tab-panel">
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="spinner"></div>
                <p style={{ marginTop: '20px', color: 'rgba(255, 255, 255, 0.7)' }}>Loading diagnostic plots...</p>
              </div>
            </div>
          ) : error ? (
            <div className="tab-panel">
              <div className="error">
                <strong>Error:</strong> {error}
              </div>
            </div>
          ) : (
            diagnosticTabs.map((tab) => {
              if (activeTab !== tab.key) return null;
              
              // Build the correct cache key based on tab type and current settings
              let cacheKey = tab.key;
              if (tab.key === 'accuracy') {
                cacheKey = `accuracy_${datasetType}_${classType}`;
              } else if (tab.key === 'auc') {
                cacheKey = `auc_${datasetType}_${classType}`;
              }
              
              const data = plotData[cacheKey];
              
              if (!data) {
                return (
                  <div key={tab.key} className="tab-panel">
                    <p style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)', padding: '40px' }}>
                      {tab.enabled ? 'Loading plot...' : 'This diagnostic plot is not yet available.'}
                    </p>
                  </div>
                );
              }
              
              return (
                <div key={tab.key} className="tab-panel">
                  <div className="plot-container">
                    <Plot
                      data={data?.data || []}
                      layout={{
                        ...(data?.layout || {}),
                        ...((tab.key === 'accuracy' || tab.key === 'auc') && {
                          dragmode: false
                        })
                      }}
                      config={{
                        responsive: true,
                        displayModeBar: true,
                        displaylogo: false,
                        modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d', 'autoScale2d'],
                        mathjax: 'cdn'  // Enable MathJax for LaTeX rendering
                      }}
                      style={{ 
                        width: '100%', 
                        height: '100%',
                        ...((tab.key === 'accuracy' || tab.key === 'auc') && { cursor: 'pointer' })
                      }}
                      onClick={(tab.key === 'accuracy' || tab.key === 'auc') ? handlePlotClick : undefined}
                      onHover={(tab.key === 'accuracy' || tab.key === 'auc') ? handlePlotHover : (tab.key === 'backsplash_fraction' ? handleBacksplashFractionHover : undefined)}
                      onUnhover={tab.key === 'backsplash_fraction' ? handleBacksplashFractionUnhover : undefined}
                      divId={tab.key === 'backsplash_fraction' ? 'backsplash-fraction-plot' : undefined}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="actions">
        <button onClick={onBack} className="button button-secondary">
          ← Back
        </button>
        <button onClick={handleContinue} className="button">
          Continue to Results →
        </button>
      </div>
      
      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmContinue}
        onCancel={handleCancelContinue}
        threshold={threshold}
      />
    </div>
  );
}

export default DiagnosticPlots;
